import { getGifHeight, getGifWidth } from '@giphy/js-util'
import { SearchContext, Video } from '@giphy/react-components'
import copy from 'copy-text-to-clipboard'
import React, { DragEvent, useContext, useEffect, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import { MARGIN } from '../../constants'
import { AppContext, SearchMode } from '../../context'
import { backButton, dndButton, linkButton } from '../../img/images'
import GifDisplay from '../gif-display'
import Overlay from '../grid-overlay'
import { Code, Download } from '../overlay-icons'
import Attribution from './footer'
import {
    AttributionContainer,
    Back,
    ClipTips,
    Container,
    CTAButton,
    DetailGifContainer,
    fadeOutTime,
    GifContainer,
    Header,
    Instructions,
} from './sc'

const MAX_GIF_HEIGHT = 310 // magic number!

const headerTitle: { [key in SearchMode]: string } = {
    gifs: 'GIF',
    stickers: 'Sticker',
    emoji: 'Emoji',
    text: 'Text',
    videos: 'Clip',
}

const disableDownload = true

const GifDetail = () => {
    const {
        displayGif: gif,
        setDisplayGif,
        searchMode,
        videoMuted,
        setVideoMuted,
        config: { width: appWidth },
    } = useContext(AppContext)

    const { term, activeChannel } = useContext(SearchContext)

    const [copiedAnimation, setCopiedAnimation] = useState<'code' | 'download' | 'link' | undefined>(undefined)
    const [isVisible, setIsVisible] = useState<boolean>(false)

    const doCopy = (copyUrl: string, button: 'link' | 'code') => {
        const copied = copy(copyUrl)
        if (copied) setCopiedAnimation(button)
    }

    useEffect(() => {
        const timeout = setTimeout(() => setCopiedAnimation(undefined), 1000)
        return () => clearTimeout(timeout)
    }, [copiedAnimation])

    const goBack = () => setIsVisible(false)

    useEffect(() => {
        if (term || activeChannel) {
            // give the grid a sec to render
            const timeout = setTimeout(goBack, 300)
            return () => clearTimeout(timeout)
        }
    }, [term, activeChannel])

    useEffect(() => {
        // after fade out, clear the gif (gif change isn't enough, if you click the same gif twice)
        if (!isVisible) {
            const timeout = setTimeout(() => setDisplayGif(undefined), fadeOutTime)
            return () => clearTimeout(timeout)
        }
    }, [isVisible, setDisplayGif])
    useEffect(() => {
        if (gif) {
            setIsVisible(true)
        } else {
            goBack()
        }
    }, [gif])
    let width = appWidth - MARGIN * 2
    if (gif) {
        const height = getGifHeight(gif, width)
        if (height > MAX_GIF_HEIGHT) {
            width = getGifWidth(gif, MAX_GIF_HEIGHT)
        }
    }
    return (
        <Container
            isVisible={isVisible}
            isStickerGrid={searchMode === 'stickers' || searchMode === 'text'}
            appWidth={appWidth}
        >
            <Header>
                <Back onClick={goBack} src={backButton} />
                <div>{headerTitle[searchMode]}</div>
            </Header>
            <GifContainer>
                {gif && (
                    <DetailGifContainer>
                        {gif.type !== 'video' ? (
                            <img src={gif.images.original.url} key={gif.id} width={width} />
                        ) : (
                            <div
                                onDragStart={(e: DragEvent<HTMLDivElement>) => {
                                    const text = ReactDOMServer.renderToStaticMarkup(<GifDisplay gif={gif} />)
                                    e.dataTransfer?.setData('text/html', text)
                                    e.dataTransfer?.setData('text/plain', text)
                                }}
                                draggable
                            >
                                <Video
                                    gif={gif}
                                    width={width}
                                    muted={videoMuted}
                                    controls
                                    hideMute
                                    hidePlayPause
                                    onUserMuted={setVideoMuted}
                                />
                            </div>
                        )}
                        <Overlay gif={gif} isHovered isDetail />
                    </DetailGifContainer>
                )}
            </GifContainer>
            {gif && (
                <AttributionContainer>
                    <Attribution gif={gif} />
                    {searchMode !== 'text' && (
                        <CTAButton
                            copiedAnimation={copiedAnimation === 'link'}
                            onClick={() => doCopy(gif.bitly_url || gif.url, 'link')}
                        >
                            <img src={linkButton} />
                        </CTAButton>
                    )}
                </AttributionContainer>
            )}
            <Instructions>
                {searchMode === 'videos' && !disableDownload ? (
                    <ClipTips>
                        <div>
                            Drag &amp; Drop or click <Code /> for a GIF preview
                        </div>
                        <div>
                            Click download&nbsp;
                            <Download />
                            &nbsp;to save a clip
                        </div>
                    </ClipTips>
                ) : (
                    <>
                        <img src={dndButton} height={18} />
                        <b>Drag &amp; Drop</b>&nbsp;into some websites
                    </>
                )}
            </Instructions>
        </Container>
    )
}

export default GifDetail
